import { useCallback, useEffect } from 'react';
import { FieldPath } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LoadingOverlay } from '@givebutter/primitives/loading-overlay';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { addNewActivity, updateActivity } from 'api/contacts/activities';
import * as Dialog from 'components/dialog';
import { useGivebutterForm } from 'components/form';
import * as Form from 'components/form';
import { ContactType, requiresSubject } from 'constants/contacts';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useForceRender } from 'customHooks/useForceRender';
import { dateToServerDateTime, isoDateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Contact, ManualItemActivity } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const getApiData = (option: string, { note, subject, occurred_at }: any) => {
    if (!requiresSubject.has(option)) {
        return {
            note,
            occurred_at,
        };
    }

    return {
        subject,
        note,
        occurred_at,
    };
};

interface AddEventProps {
    contact: Pick<
        Contact,
        'id' | 'first_name' | 'last_name' | 'company_name' | 'type' | 'name_display'
    >;
    item?: ManualItemActivity;
    children?: React.ReactNode;
    onSuccess?: () => void;
    isFirstTimeSupporterList?: boolean;
}

type AddEventForm = {
    date: DateTime;
    note: string;
    subject: string;
    option: string;
};

const AddEvent = ({
    children,
    contact,
    item,
    onSuccess,
    isFirstTimeSupporterList,
}: AddEventProps) => {
    const { key, forceRender } = useForceRender();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const toast = useToast();
    const isEdit = !!item;
    const contactName =
        contact.type === ContactType.Company ? contact.company_name : contact.name_display;

    const getDefaultValues = useCallback(() => {
        const option = item?.type || 'note';

        if (!isEdit) {
            return {
                note: '',
                option,
                date: DateTime.now(),
            };
        }

        return {
            option,
            date: isoDateFromServer(item.occurred_at),
            note: item.data.note,
            subject: item.data.subject,
        };
    }, [isEdit, item]);

    const methods = useGivebutterForm<AddEventForm>({
        defaultValues: getDefaultValues(),
    });

    const { setError, control, path, handleSubmit, reset } = methods;

    const option = methods.watch('option');

    useEffect(() => {
        reset(getDefaultValues());
    }, [getDefaultValues, reset]);

    const { mutate, isPending: isLoading } = useMutation({
        mutationFn: ({ option, note, subject, date }: AddEventForm) => {
            const data = getApiData(option, {
                note,
                subject,
                occurred_at: dateToServerDateTime(date || DateTime.now()),
            });

            if (isEdit) {
                return updateActivity(accountId, contact.id, {
                    activityId: item.id,
                    data,
                });
            }

            return addNewActivity(accountId, contact.id, {
                type: option,
                data: { ...data, is_first_time_supporter_list: isFirstTimeSupporterList },
            });
        },
        onSuccess: () => {
            reset(getDefaultValues());
            toast.success(`Contact event ${isEdit ? 'updated' : 'added'}.`);
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.contacts(accountId) });
            onSuccess?.();
            forceRender();
        },
        onError: (error: GivebutterAxiosError) => {
            const errors = error.response?.data.errors;
            if (errors) {
                Object.keys(errors).forEach(key => {
                    const message = errors[key][0];
                    setError(key as FieldPath<AddEventForm>, {
                        message,
                    });
                });
            }

            toast.error(error.response?.data?.message ?? 'Something went wrong.');
        },
    });

    const onSubmit = (value: AddEventForm) => {
        mutate(value);
    };

    return (
        <Dialog.Root key={key} onOpenChange={open => !open && reset()}>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content>
                    <Form.Provider {...methods}>
                        <LoadingOverlay isLoading={isLoading}>
                            <Dialog.Body className="flex flex-col gap-4">
                                <Dialog.Title>
                                    {isEdit ? 'Edit' : 'Add a new'} event to {contactName}’s
                                    timeline
                                </Dialog.Title>
                                <Dialog.Description>
                                    <form
                                        onSubmit={handleSubmit(onSubmit)}
                                        className="flex w-full flex-col gap-4"
                                    >
                                        {!isEdit && (
                                            <Form.Container>
                                                <Form.Container>
                                                    <Form.Label name={path('option')}>
                                                        Event Type
                                                    </Form.Label>
                                                    <Form.SingleSelect
                                                        name="option"
                                                        control={control}
                                                    >
                                                        <SingleSelect.Trigger aria-label="Event type" />
                                                        <SingleSelect.Content>
                                                            <SingleSelect.Item value="note">
                                                                <SingleSelect.ItemText>
                                                                    Note
                                                                </SingleSelect.ItemText>
                                                            </SingleSelect.Item>
                                                            <SingleSelect.Item value="email">
                                                                <SingleSelect.ItemText>
                                                                    Email
                                                                </SingleSelect.ItemText>
                                                            </SingleSelect.Item>
                                                            <SingleSelect.Item value="phone_call">
                                                                <SingleSelect.ItemText>
                                                                    Phone call
                                                                </SingleSelect.ItemText>
                                                            </SingleSelect.Item>
                                                            <SingleSelect.Item value="meeting">
                                                                <SingleSelect.ItemText>
                                                                    Meeting
                                                                </SingleSelect.ItemText>
                                                            </SingleSelect.Item>
                                                            <SingleSelect.Item value="sms">
                                                                <SingleSelect.ItemText>
                                                                    Text message
                                                                </SingleSelect.ItemText>
                                                            </SingleSelect.Item>
                                                            <SingleSelect.Item value="completed_task">
                                                                <SingleSelect.ItemText>
                                                                    Completed task
                                                                </SingleSelect.ItemText>
                                                            </SingleSelect.Item>
                                                            <SingleSelect.Item value="volunteer_activity">
                                                                <SingleSelect.ItemText>
                                                                    Volunteer activity
                                                                </SingleSelect.ItemText>
                                                            </SingleSelect.Item>
                                                        </SingleSelect.Content>
                                                    </Form.SingleSelect>
                                                </Form.Container>
                                            </Form.Container>
                                        )}
                                        <Form.Container>
                                            <Form.Label name={path('date')}>Date</Form.Label>
                                            <Form.UseFormDatePicker
                                                control={control}
                                                name={path('date')}
                                            />
                                        </Form.Container>
                                        {requiresSubject.has(option) && (
                                            <Form.Container>
                                                <Form.Label name={path('subject')}>
                                                    Subject
                                                    <Form.LabelAsterisk />
                                                </Form.Label>
                                                <Form.TextInputRoot
                                                    control={control}
                                                    name={path('subject')}
                                                >
                                                    <Form.TextInputForm placeholder="Subject" />
                                                </Form.TextInputRoot>
                                                <Form.Error name={path('subject')} />
                                            </Form.Container>
                                        )}

                                        <Form.Container>
                                            <Form.Label name={path('note')}>
                                                Notes
                                                {!requiresSubject.has(option) && (
                                                    <Form.LabelAsterisk />
                                                )}
                                            </Form.Label>
                                            <Form.Textarea
                                                control={control}
                                                name={path('note')}
                                                placeholder="Notes"
                                                rows={5}
                                            />
                                            <Form.Error name={path('note')} />
                                        </Form.Container>
                                        <Form.SubmitButton disabled={isLoading}>
                                            {isEdit ? 'Save Event' : 'Add Event'}
                                        </Form.SubmitButton>
                                    </form>
                                </Dialog.Description>
                            </Dialog.Body>
                        </LoadingOverlay>
                    </Form.Provider>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const Root = AddEvent;

const AddEventTrigger = Dialog.Trigger;
const Trigger = AddEventTrigger;

export {
    Root,
    Trigger,
    //
    AddEvent,
    AddEventTrigger,
};
